import React, { useContext, useEffect, useRef, useState } from 'react'
import Button from 'components/button'
import { Delete, Pencil, Plus } from 'components/icons'
import Tooltip from 'components/tooltip'
import DataTable, { dataTableColumns } from 'components/data-table'
import RowActions from 'components/data-table/rowActions'
import NotificationResourceList from './components/NotificationResourceList'
import WebhookDialog, {
  IFormValues as INotificationFormValues
} from 'views/settings/components/notifications/components/WebhookDialog'
import CountPopover, { CodeItem } from 'views/settings/components/notifications/components/CountPopover'
import ActionDialog from 'components/dialog/action'
import api from 'helpers/api'
import { PROJECT } from 'app-constants'
import { useApplicationStore } from 'hooks/application'
import { INotification } from 'types'
import useProject from 'hooks/useProject'
import { useShownHub } from 'hooks/useShownHub'
import { eventCount, eventLabelsForWebhook } from 'views/settings/components/notifications/helpers/utils'
import { SETTINGS_CANVAS, SettingsCard, TextLink } from 'views/settings/components/settingsCard'

const BY_DESCRIPTION = [{ id: 'description', desc: false }]

/** The form's values as the API takes them: the chosen documents and tables by id. */
const toRequest = (values: INotificationFormValues) => ({
  ...values,
  tables: values.tables.map((table) => table.publicId),
  processes: values.processes.map((process) => process.publicId)
})

const webhookUrl = (notification: INotification) => notification.actions[0]?.webhookUrl ?? ''

/** What a row's Edit and Delete need from the page. */
const WebhookActions = React.createContext<{
  edit: (notification: INotification) => void
  remove: (notification: INotification) => void
}>({ edit: () => undefined, remove: () => undefined })

/** Text cut short to its column, with the whole of it on hover. */
const Truncated: React.FC<{ text: string; full?: string; className: string }> = ({ text, full = text, className }) => (
  <Tooltip content={full} showOnlyIfTruncated delay={300}>
    <span className={`block truncate ${className}`}>{text}</span>
  </Tooltip>
)

/** "1 key-value", "3 key-values". */
const headerCount = (count: number) => `${count} ${count === 1 ? 'key-value' : 'key-values'}`

/** A webhook's events as a count, "3 events", opening a list of them. */
const EventsCell: React.FC<{ notification: INotification }> = ({ notification }) => {
  const events = eventLabelsForWebhook(notification.triggers)
  if (events.length === 0) return <span className="text-muted">None</span>
  return (
    <CountPopover
      count={eventCount(events.length)}
      title="Events"
      of={notification.description}
      id={`webhook-events-${notification.publicId}`}
      rows={events.length}
    >
      {events.map((event) => (
        <CodeItem key={event}>{event}</CodeItem>
      ))}
    </CountPopover>
  )
}

/** A webhook's custom headers as a count, "1 key-value", opening a list of them as "Key: value". */
const HeadersCell: React.FC<{ notification: INotification }> = ({ notification }) => {
  const headers = notification.actions[0]?.customHeaders ?? []
  if (headers.length === 0) return <span className="text-muted">None</span>
  return (
    <CountPopover
      count={headerCount(headers.length)}
      title="Headers"
      of={notification.description}
      id={`webhook-headers-${notification.publicId}`}
      rows={headers.length}
    >
      {headers.map((header, index) => (
        <CodeItem key={index}>
          {header.key}: {header.value}
        </CodeItem>
      ))}
    </CountPopover>
  )
}

const ActionsCell: React.FC<{ notification: INotification }> = ({ notification }) => {
  const { edit, remove } = useContext(WebhookActions)
  return (
    <RowActions
      name={notification.description}
      actions={[
        { label: 'Edit', icon: <Pencil />, onSelect: () => edit(notification) },
        { label: 'Delete', icon: <Delete />, destructive: true, onSelect: () => remove(notification) }
      ]}
    />
  )
}

// Set once, here: a cell is a component, so columns made afresh on each render would remount every cell.
const col = dataTableColumns<INotification>()
const COLUMNS = col.columns([
  col.accessor('description', {
    header: 'Description',
    sortFn: 'alphanumeric',
    cell: ({ getValue }) => <Truncated text={getValue()} className="max-w-[180px] font-medium" />
  }),
  col.accessor(webhookUrl, {
    id: 'url',
    header: 'URL',
    sortFn: 'alphanumeric',
    cell: ({ getValue }) => <Truncated text={getValue()} className="max-w-[180px]" />
  }),
  col.accessor((notification) => notification.actions[0]?.customHeaders.length ?? 0, {
    id: 'customHeaders',
    header: 'Headers',
    sortFn: 'alphanumeric',
    cell: ({ row }) => <HeadersCell notification={row.original} />
  }),
  col.display({
    id: 'events',
    header: 'Events',
    cell: ({ row }) => <EventsCell notification={row.original} />
  }),
  col.display({
    id: 'resources',
    header: 'Resources',
    meta: { className: 'whitespace-normal' },
    cell: ({ row }) => <NotificationResourceList notification={row.original} />
  }),
  col.display({
    id: 'actions',
    header: () => <span className="sr-only">Actions</span>,
    meta: { className: 'w-0 text-right' },
    cell: ({ row }) => <ActionsCell notification={row.original} />
  })
])

/**
 * The hub's webhooks, by description, to add, edit and delete. Each one sends event data to its URL when
 * one of its events happens in the hub, for all documents and tables or only the ones chosen.
 */
const DevelopersBox: React.FC = () => {
  const { project } = useProject()
  const { displayErrorMessage } = useApplicationStore()
  const projectId = project.publicId

  const [notifications, setNotifications] = useState<INotification[]>()
  const [loadFailed, setLoadFailed] = useState(false)
  const [adding, setAdding] = useState(false)
  const [editing, setEditing] = useState<INotification | null>(null)
  const [deleting, setDeleting] = useState<INotification | null>(null)
  // Which load is the latest, so only it fills the list: one still on its way from before (another hub,
  // or an earlier load) is dropped.
  const latestLoad = useRef(0)
  const stillShown = useShownHub(projectId)

  /** Loads the list. A reload after a change keeps the current rows in place until the new ones arrive. */
  const load = (keepRows: boolean) => {
    // Asked for by a change started in the hub before, that finished after switching: not here.
    if (!stillShown(projectId)) return
    latestLoad.current += 1
    const thisLoad = latestLoad.current
    if (!keepRows) setNotifications(undefined)
    setLoadFailed(false)
    api
      .getNotifications(projectId)
      .then((response) => {
        if (thisLoad === latestLoad.current) setNotifications(response.data)
      })
      .catch((error) => {
        if (thisLoad !== latestLoad.current) return
        if (keepRows) displayErrorMessage(error)
        else setLoadFailed(true)
      })
  }

  // Another hub's settings: its own list, with no dialog left open from the hub before.
  useEffect(() => {
    setAdding(false)
    setEditing(null)
    setDeleting(null)
    if (projectId !== '') load(false)
  }, [projectId])

  const handleCreateNotification = async (values: INotificationFormValues) => {
    try {
      await api.createNotification(projectId, toRequest(values))
    } catch (e) {
      if (stillShown(projectId)) displayErrorMessage(e)
      return
    }
    if (!stillShown(projectId)) return
    setAdding(false)
    load(true)
  }

  const handleUpdateNotification = async (values: INotificationFormValues) => {
    if (!editing) return
    try {
      await api.updateNotification(editing, toRequest(values))
    } catch (e) {
      if (stillShown(projectId)) displayErrorMessage(e)
      return
    }
    if (!stillShown(projectId)) return
    setEditing(null)
    load(true)
  }

  const handleDeleteNotification = async (notification: INotification): Promise<void> => {
    try {
      await api.deleteNotification(notification.publicId)
    } catch (e) {
      if (stillShown(projectId)) displayErrorMessage(e)
      throw e
    }
    if (!stillShown(projectId)) return
    setNotifications((current) => current?.filter((each) => each.publicId !== notification.publicId))
  }

  return (
    <div className={`w-full min-h-full overflow-auto ${SETTINGS_CANVAS}`}>
      <div className="flex flex-col gap-4 max-w-[960px] mx-auto">
        <SettingsCard
          title="Webhooks"
          description={`Configure webhooks to send events from this ${PROJECT} to other services.`}
        >
          <div className="flex justify-end px-4 py-3">
            <Button internalType="outline" data-tour="add-webhook" onClick={() => setAdding(true)}>
              <Plus />
              Add Webhook
            </Button>
          </div>
          <WebhookActions.Provider value={{ edit: setEditing, remove: setDeleting }}>
            <DataTable
              label="Webhooks"
              columns={COLUMNS}
              data={notifications}
              getRowId={(notification) => notification.publicId}
              initialSort={BY_DESCRIPTION}
              paged={false}
              empty={
                <>
                  <div className="font-medium text-body">No webhooks</div>
                  <div className="mt-0.5">
                    A webhook sends event data to a URL of your choice when something happens in this hub, such as a
                    table cell being updated.
                  </div>
                </>
              }
              error={
                loadFailed && (
                  <>
                    <div className="font-medium text-body">Webhooks couldn&apos;t be loaded</div>
                    <div className="mt-1">
                      <TextLink onClick={() => load(false)}>Try again</TextLink>
                    </div>
                  </>
                )
              }
            />
          </WebhookActions.Provider>
        </SettingsCard>
      </div>

      {adding && (
        <WebhookDialog project={project} onClose={() => setAdding(false)} onSubmit={handleCreateNotification} />
      )}

      {editing && (
        <WebhookDialog
          project={project}
          notification={editing}
          onClose={() => setEditing(null)}
          onSubmit={handleUpdateNotification}
        />
      )}

      {deleting && (
        <ActionDialog
          title={`Delete ${deleting.description}?`}
          description={`Events will no longer be sent to ${webhookUrl(deleting)}. This cannot be undone.`}
          confirmLabel="Delete Webhook"
          danger
          phrase={deleting.description}
          copyable
          onClose={() => setDeleting(null)}
          onConfirm={() => handleDeleteNotification(deleting)}
        />
      )}
    </div>
  )
}

export default DevelopersBox
